import Constants from 'expo-constants'
import { useEffect, useMemo, useRef } from 'react'
import { AppState, AppStateStatus } from 'react-native'
import { coerce } from 'semver'

import trpc from '@/utils/trpc'

export interface VersionCheckResult {
  currentVersion?: string
  isUpdateRequired: boolean
  minVersion: string
}

export const useVersionCheck = () => {
  const appState = useRef<AppStateStatus>(AppState.currentState)

  const currentVersion = Constants.expoConfig?.version

  const {
    data,
    error: queryError,
    isLoading,
    refetch,
  } = trpc.versionCheck.useQuery(undefined, {
    retry: 3,
    retryDelay: 1000,
  })

  const versionCheckResult = useMemo<VersionCheckResult | null>(() => {
    if (data) {
      const minVersion = data.minVersion

      if (!currentVersion || !minVersion) {
        return {
          ...data,
          isUpdateRequired: false,
        }
      }

      const currentVersionSemVer = coerce(currentVersion)
      const isUpdateRequired =
        (currentVersionSemVer?.compare(minVersion) || 1) < 0

      return {
        ...data,
        isUpdateRequired: isUpdateRequired || false,
        currentVersion,
      }
    }

    if (queryError) {
      // If version check fails, assume no update required to avoid blocking users
      return {
        currentVersion,
        isUpdateRequired: false,
        minVersion: '0.0.0',
      }
    }

    return null
  }, [currentVersion, data, queryError])

  const error = queryError?.message ?? null

  useEffect(() => {
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      const wasInBackground =
        appState.current === 'inactive' || appState.current === 'background'

      if (wasInBackground && nextAppState === 'active') {
        refetch()
      }

      appState.current = nextAppState
    }

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    )

    return () => {
      subscription.remove()
    }
  }, [refetch])

  return {
    versionCheckResult,
    isLoading,
    error,
    refetch,
  }
}
